import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  Tabs,
  Row,
  Col,
  Statistic,
  Progress,
  Tooltip,
  Divider,
  List,
  Badge,
  Avatar,
  Descriptions,
  message,
  Popconfirm,
  Alert,
  Steps,
  Radio,
  Slider,
  Upload,
  Tree,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SettingOutlined,
  RobotOutlined,
  PlayCircleOutlined,
  CopyOutlined,
  ApiOutlined,
  BookOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  DownloadOutlined,
  PoweroffOutlined,
  CheckCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const AgentManagement: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [testModalVisible, setTestModalVisible] = useState(false);
  const [knowledgeModalVisible, setKnowledgeModalVisible] = useState(false);
  const [deployModalVisible, setDeployModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [testResults, setTestResults] = useState<any[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [agents, setAgents] = useState<any[]>([]);
  const [domainModalVisible, setDomainModalVisible] = useState(false);
  const [styleModalVisible, setStyleModalVisible] = useState(false);
  const [domainForm] = Form.useForm();
  const [styleForm] = Form.useForm();
  // 领域编辑上下文（支持一级/二级）
  const [editingDomainLevel, setEditingDomainLevel] = useState<'level1'|'level2'>('level1');
  const [editingDomainParentIndex, setEditingDomainParentIndex] = useState<number | null>(null);
  const [editingStyleType, setEditingStyleType] = useState<'primary'|'secondary'>('primary');
  const [editingDomainIndex, setEditingDomainIndex] = useState<number | null>(null);
  const [editingStyleIndex, setEditingStyleIndex] = useState<number | null>(null);

  // 新增智能体
  const handleAddAgent = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  // 编辑智能体
  const handleEditAgent = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  // 保存智能体
  const handleSaveAgent = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('智能体更新成功！');
      } else {
        message.success('智能体创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除智能体
  const handleDeleteAgent = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`已删除智能体：${record.name}`);
    } catch (error) {
      message.error('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 查看智能体详情
  const handleViewAgent = (record: any) => {
    setSelectedRecord(record);
    setConfigModalVisible(true);
  };

  // 测试智能体
  const handleTestAgent = (record: any) => {
    setSelectedRecord(record);
    setTestResults([]);
    setCurrentStep(0);
    setTestModalVisible(true);
  };

  // 复制智能体
  const handleCopyAgent = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success(`已复制智能体：${record.agentName}`);
    } catch (error) {
      message.error('复制失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 启用/禁用智能体
  const handleToggleStatus = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800));
      const newStatus = record.status === 'online' ? 'offline' : 'online';
      message.success(`智能体已${newStatus === 'online' ? '启用' : '禁用'}`);
    } catch (error) {
      message.error('操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 部署智能体
  const handleDeployAgent = (record: any) => {
    setSelectedRecord(record);
    setDeployModalVisible(true);
  };

  // 知识库管理
  const handleKnowledgeBase = (record: any) => {
    setSelectedRecord(record);
    setKnowledgeModalVisible(true);
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的智能体');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 个智能体`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 运行测试
  const handleRunTest = async () => {
    setLoading(true);
    try {
      const steps = [
        { step: 0, title: '初始化智能体', status: 'process' },
        { step: 1, title: '加载知识库', status: 'process' },
        { step: 2, title: '执行对话测试', status: 'process' },
        { step: 3, title: '验证响应质量', status: 'process' },
        { step: 4, title: '生成测试报告', status: 'finish' },
      ];
      
      for (let i = 0; i < steps.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 800));
        setCurrentStep(i);
        setTestResults(prev => [...prev, steps[i]]);
      }
      
      message.success('智能体测试完成！');
    } catch (error) {
      message.error('测试失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 模拟智能体数据（包含主/副领域与风格配置）
  const agentData = [
    // P0：主播助手Bot
    {
      id: 1,
      agentId: 'AGENT_HOST_ASSIST',
      code: 'host_assistant_bot',
      name: '主播助手Bot',
      type: '主播助手',
      description: '直播间公域发言；平台/活动/游戏介绍与引导',
      capabilities: ['平台介绍', '活动介绍', '游戏讲解'],
      knowledgeBase: '平台/活动/游戏规则库',
      status: 'online',
      priority: 10,
      maxConcurrent: 5,
      usageCount: 1520,
      successRate: 95.2,
      avgResponseTime: 1.3,
      lastActive: '2025-10-23 14:30',
      createdAt: '2025-10-20',
      tags: ['P0', '公域', '介绍'],
      promptTemplate: '你是直播间主播助手，负责平台/活动/游戏介绍与引导。',
      // 一级领域 + 二级领域（children）
      domains: [
        {
          domain_code: 'platform_intro',
          domain_name: '平台介绍',
          priority: 10,
          knowledge_base_ids: ['kb_platform_001','kb_platform_002'],
          children: [
            { domain_code: 'vip_intro', domain_name: 'VIP介绍', priority: 8, knowledge_base_ids: ['kb_vip_001'] },
            { domain_code: 'recharge_guide', domain_name: '充值引导', priority: 7, knowledge_base_ids: ['kb_recharge_001'] },
          ],
        },
        {
          domain_code: 'activity_intro',
          domain_name: '活动介绍',
          priority: 10,
          knowledge_base_ids: ['kb_activity_001','kb_activity_002'],
          children: [],
        },
        {
          domain_code: 'game_intro',
          domain_name: '游戏介绍',
          priority: 10,
          knowledge_base_ids: ['kb_game_001','kb_game_002'],
          children: [],
        },
      ],
      styles: {
        primary: [
          { style_code: 'official', style_name: '官方风格', weight: 50, script_ids: ['MSG041','MSG042','MSG046','MSG047','MSG051'] },
          { style_code: 'warm', style_name: '热情风格', weight: 30, script_ids: ['MSG036','MSG037','MSG071'] },
          { style_code: 'concise', style_name: '简洁风格', weight: 20, script_ids: ['MSG076','MSG077'] },
        ],
        secondary: [
          { style_code: 'professional', style_name: '专业风格', weight: 10, script_ids: ['MSG106','MSG107'] },
        ],
      },
    },
    // P0：气氛组Bot
    {
      id: 2,
      agentId: 'AGENT_HYPE',
      code: 'hype_team_bot',
      name: '气氛组Bot',
      type: '气氛活跃',
      description: '直播间公域发言；暖场、起哄、吐槽、互动带节奏',
      capabilities: ['氛围营造', '互动引导', '情绪调动'],
      knowledgeBase: '氛围/互动话术库',
      status: 'online',
      priority: 10,
      maxConcurrent: 8,
      usageCount: 2104,
      successRate: 93.8,
      avgResponseTime: 0.9,
      lastActive: '2025-10-23 14:28',
      createdAt: '2025-10-20',
      tags: ['P0', '公域', '活跃'],
      promptTemplate: '你是直播间气氛组，负责暖场、起哄与互动带节奏。',
      domains: [
        {
          domain_code: 'atmosphere_building',
          domain_name: '氛围营造',
          priority: 10,
          knowledge_base_ids: ['kb_atmosphere_001'],
          children: [],
        },
        {
          domain_code: 'interaction_guide',
          domain_name: '互动引导',
          priority: 10,
          knowledge_base_ids: ['kb_interaction_001'],
          children: [
            { domain_code: 'topic_seed', domain_name: '话题种子', priority: 7, knowledge_base_ids: ['kb_topic_001'] },
            { domain_code: 'qa_prompt', domain_name: '提问引导', priority: 7, knowledge_base_ids: [] },
          ],
        },
        {
          domain_code: 'emotion_stimulation',
          domain_name: '情绪调动',
          priority: 10,
          knowledge_base_ids: [],
          children: [],
        },
        {
          domain_code: 'user_support',
          domain_name: '用户支持',
          priority: 6,
          knowledge_base_ids: ['kb_faq_001'],
          children: [
            { domain_code: 'quick_tips', domain_name: '快捷提示', priority: 5, knowledge_base_ids: [] },
          ],
        },
        {
          domain_code: 'trend_follow',
          domain_name: '话题跟随',
          priority: 5,
          knowledge_base_ids: ['kb_trend_001'],
          children: [
            { domain_code: 'meme_follow', domain_name: '梗跟随', priority: 5, knowledge_base_ids: [] },
          ],
        },
      ],
      styles: {
        primary: [
          { style_code: 'funny', style_name: '搞笑风格', weight: 40, script_ids: ['MSG026','MSG061','MSG096'] },
          { style_code: 'hype', style_name: '热血风格', weight: 30, script_ids: ['MSG104','MSG108'] },
          { style_code: 'roast', style_name: '吐槽风格', weight: 20, script_ids: ['MSG098','MSG099','MSG100'] },
          { style_code: 'warm', style_name: '温暖风格', weight: 10, script_ids: ['MSG036','MSG037'] },
        ],
        secondary: [
          { style_code: 'edge', style_name: '擦边风格', weight: 5, script_ids: ['MSG031','MSG066'], enable_conditions: { whitelist_only: true, safety: 'strict' } },
        ],
      },
    },
    // P0：客服Bot
    {
      id: 3,
      agentId: 'AGENT_CS',
      code: 'customer_service_bot',
      name: '客服Bot',
      type: '客服',
      description: '私聊/客服会话；FAQ、自助解答、分流与转人工',
      capabilities: ['问题解答', '账户服务', '问题引导'],
      knowledgeBase: 'FAQ/账户/流程知识库',
      status: 'online',
      priority: 10,
      maxConcurrent: 12,
      usageCount: 2980,
      successRate: 98.0,
      avgResponseTime: 0.7,
      lastActive: '2025-10-23 14:26',
      createdAt: '2025-10-20',
      tags: ['P0', '私聊', '服务'],
      promptTemplate: '你是智能客服，优先准确解答并在需要时转人工。',
      domains: [
        {
          domain_code: 'faq_answer',
          domain_name: 'FAQ解答',
          priority: 10,
          knowledge_base_ids: ['kb_faq_001','kb_faq_002'],
          children: [
            { domain_code: 'activity_query', domain_name: '活动查询', priority: 9, knowledge_base_ids: ['kb_activity_001'] },
            { domain_code: 'game_guidance', domain_name: '游戏指导', priority: 9, knowledge_base_ids: ['kb_game_001'] },
            { domain_code: 'complaint_handling', domain_name: '投诉处理', priority: 9, knowledge_base_ids: ['kb_complaint_001'] },
            { domain_code: 'vip_service', domain_name: 'VIP服务', priority: 8, knowledge_base_ids: ['kb_vip_001'] },
          ],
        },
        {
          domain_code: 'account_service',
          domain_name: '账户服务',
          priority: 10,
          knowledge_base_ids: ['kb_account_001'],
          children: [],
        },
        {
          domain_code: 'problem_guidance',
          domain_name: '问题引导',
          priority: 10,
          knowledge_base_ids: ['kb_guide_001'],
          children: [],
        },
      ],
      styles: {
        primary: [
          { style_code: 'official', style_name: '官方风格', weight: 60, script_ids: ['MSG189','MSG190','MSG194'] },
          { style_code: 'concise', style_name: '简洁风格', weight: 30, script_ids: ['MSG194','MSG198'] },
          { style_code: 'warm', style_name: '温暖风格', weight: 10, script_ids: ['MSG190','MSG197'] },
        ],
        secondary: [
          { style_code: 'professional', style_name: '专业风格', weight: 5, script_ids: ['MSG199','MSG200'] },
        ],
      },
    },
  ];

  useEffect(() => {
    // 初始化可编辑数据源
    setAgents(agentData);
  }, []);

  const columns = [
    {
      title: '智能体信息',
      key: 'agentInfo',
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Avatar size="large" icon={<RobotOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{record.name}</div>
            <div style={{ fontSize: 12, color: '#999' }}>{record.code || record.agentId}</div>
          </div>
        </Space>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (description: string) => (
        <Tooltip title={description}>
          <span>{description}</span>
        </Tooltip>
      ),
    },
    {
      title: '领域',
      key: 'domains',
      width: 150,
      render: (_: any, record: any) => (
        <Tooltip title={`${(record.domains || []).length}个领域`}>
          <Space wrap>
            {(record.domains || []).slice(0, 2).map((domain: any, index: number) => (
              <Tag key={index} color="blue">{domain.domain_name}</Tag>
            ))}
            {(record.domains || []).length > 2 && <Tag>+{(record.domains || []).length - 2}</Tag>}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={status === 'online' ? 'green' : 'red'}>
          {status === 'online' ? '在线' : '离线'}
        </Tag>
      ),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      key: 'priority',
      width: 80,
      render: (priority: number) => (
        <Badge count={priority} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      sorter: (a: any, b: any) => a.usageCount - b.usageCount,
    },
    {
      title: '成功率',
      dataIndex: 'successRate',
      key: 'successRate',
      width: 100,
      render: (rate: number) => (
        <Progress
          percent={rate}
          size="small"
          format={() => `${rate}%`}
          strokeColor={rate >= 90 ? '#52c41a' : rate >= 80 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewAgent(record)}
            >
              详情
            </Button>
          </Tooltip>
          <Tooltip title="测试智能体">
            <Button
              type="link"
              icon={<ExperimentOutlined />}
              onClick={() => handleTestAgent(record)}
            >
              测试
            </Button>
          </Tooltip>
          <Tooltip title="编辑智能体">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditAgent(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="知识库">
            <Button
              type="link"
              icon={<BookOutlined />}
              onClick={() => handleKnowledgeBase(record)}
            >
              知识库
            </Button>
          </Tooltip>
          <Tooltip title={record.status === 'online' ? '禁用智能体' : '启用智能体'}>
            <Button
              type="link"
              icon={record.status === 'online' ? <PoweroffOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleStatus(record)}
              danger={record.status === 'online'}
            >
              {record.status === 'online' ? '禁用' : '启用'}
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个智能体吗？"
            onConfirm={() => handleDeleteAgent(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除智能体">
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
              >
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  const agentStats = [
    { title: '总智能体数', value: 14, icon: <RobotOutlined /> },
    { title: '在线智能体', value: 12, icon: <RobotOutlined /> },
    { title: '今日调用', value: 3240, icon: <RobotOutlined /> },
    { title: '平均成功率', value: '92.5%', icon: <RobotOutlined /> },
  ];

  const agentTypes = [
    { type: '气氛活跃', count: 2, usage: 85 },
    { type: '产品介绍', count: 2, usage: 72 },
    { type: '客服', count: 3, usage: 91 },
    { type: '情感交流', count: 2, usage: 68 },
    { type: '搞笑', count: 1, usage: 45 },
    { type: '擦边', count: 1, usage: 38 },
    { type: '安全', count: 2, usage: 89 },
    { type: '营销', count: 1, usage: 76 },
  ];

  const handleEditSubmit = (values: any) => {
    console.log('保存智能体:', values);
    setEditModalVisible(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1>智能体管理</h1>
        <p>定义和扩展不同的智能体，承担各自的领域对话能力，实现智能体的全生命周期管理</p>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {agentStats.map((stat, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card>
              <div className="stats-card">
                <div style={{ fontSize: 32, color: '#1890ff', marginBottom: 8 }}>
                  {stat.icon}
                </div>
                <div className="stats-number">{stat.value}</div>
                <div className="stats-label">{stat.title}</div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]}>
        {/* 智能体列表 */}
        <Col xs={24} lg={16}>
          <Card
            title="智能体列表"
            extra={
              <Space>
                <Tooltip title="调度配置">
                  <Button icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
                    调度配置
                  </Button>
                </Tooltip>
                <Tooltip title="导入智能体">
                  <Button icon={<ImportOutlined />} onClick={() => message.info('导入功能开发中...')}>
                    导入
                  </Button>
                </Tooltip>
                <Tooltip title="导出智能体">
                  <Button icon={<ExportOutlined />} onClick={() => message.info('导出功能开发中...')}>
                    导出
                  </Button>
                </Tooltip>
                <Tooltip title="创建智能体">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddAgent}>
                    创建智能体
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索智能体名称或ID"
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                <Select 
                  placeholder="智能体类型" 
                  style={{ width: 150 }}
                  value={filterType}
                  onChange={setFilterType}
                >
                  <Option value="all">全部类型</Option>
                  <Option value="气氛活跃">气氛活跃</Option>
                  <Option value="产品介绍">产品介绍</Option>
                  <Option value="客服">客服</Option>
                  <Option value="情感交流">情感交流</Option>
                </Select>
                <Select 
                  placeholder="状态" 
                  style={{ width: 100 }}
                  value={filterStatus}
                  onChange={setFilterStatus}
                >
                  <Option value="all">全部状态</Option>
                  <Option value="online">在线</Option>
                  <Option value="offline">离线</Option>
                </Select>
                <Tooltip title="刷新数据">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => message.info('数据已刷新')}
                  />
                </Tooltip>
                <Tooltip title="高级筛选">
                  <Button icon={<FilterOutlined />} />
                </Tooltip>
              </Space>
            </div>

            <Table
              rowSelection={rowSelection}
              columns={columns}
              dataSource={agents}
              rowKey="id"
              scroll={{ x: 1400 }}
              pagination={{
                total: agents.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 个智能体`,
              }}
            />

            {/* 批量操作 */}
            {selectedRowKeys.length > 0 && (
              <Card style={{ marginTop: 16 }}>
                <Space>
                  <span>已选择 {selectedRowKeys.length} 个智能体</span>
                  <Tooltip title="批量启用">
                    <Button icon={<CheckCircleOutlined />} onClick={() => message.info('批量启用功能开发中...')}>
                      批量启用
                    </Button>
                  </Tooltip>
                  <Tooltip title="批量禁用">
                    <Button icon={<PoweroffOutlined />} onClick={() => message.info('批量禁用功能开发中...')}>
                      批量禁用
                    </Button>
                  </Tooltip>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 个智能体吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="批量删除">
                      <Button icon={<DeleteOutlined />} danger>
                        批量删除
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </Card>
            )}
          </Card>
        </Col>

        {/* 智能体类型统计 */}
        <Col xs={24} lg={8}>
          <Card title="智能体类型统计" extra={<Button type="link">查看全部</Button>}>
            <List
              size="small"
              dataSource={agentTypes}
              renderItem={(item) => (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>{item.type}</span>
                        <Badge count={item.count} style={{ backgroundColor: '#52c41a' }} />
                      </div>
                    }
                    description={
                      <div>
                        <div style={{ marginBottom: 4 }}>数量: {item.count}</div>
                        <Progress percent={item.usage} size="small" />
                        <span style={{ fontSize: 12, color: '#666' }}>使用率: {item.usage}%</span>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>

          <Card title="智能体能力" style={{ marginTop: 16 }}>
            <Space wrap>
              <Tag color="blue">情绪调动</Tag>
              <Tag color="green">互动引导</Tag>
              <Tag color="orange">氛围营造</Tag>
              <Tag color="purple">产品介绍</Tag>
              <Tag color="cyan">功能说明</Tag>
              <Tag color="red">问题解答</Tag>
              <Tag color="gold">客户服务</Tag>
              <Tag color="lime">情感支持</Tag>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 编辑智能体模态框 */}
      <Modal
        title={selectedRecord ? '编辑智能体' : '创建智能体'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button key="test" icon={<PlayCircleOutlined />}>
            测试
          </Button>,
          <Button key="save" type="primary" onClick={() => form.submit()}>
            保存
          </Button>,
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="智能体编码"
                rules={[{ required: true, message: '请输入智能体编码' }]}
              >
                <Input placeholder="如: host_assistant_bot" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="name"
                label="智能体名称"
                rules={[{ required: true, message: '请输入智能体名称' }]}
              >
                <Input placeholder="如: 气氛活跃Bot" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="智能体描述"
            rules={[{ required: true, message: '请输入智能体描述' }]}
          >
            <TextArea
              rows={3}
              placeholder="描述智能体的主要功能和特点"
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="启用状态"
            valuePropName="checked"
          >
            <Switch checkedChildren="启用" unCheckedChildren="禁用" />
          </Form.Item>

          <div style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 6 }}>
            <Alert
              message="提示"
              description="领域和风格配置请在保存后在详情页面进行配置"
              type="info"
              showIcon
            />
          </div>
        </Form>
      </Modal>

      {/* 领域编辑弹窗（支持一级/二级） */}
      <Modal
        title={editingDomainLevel === 'level1' ? (editingDomainIndex === null ? '添加一级领域' : '编辑一级领域') : (editingDomainIndex === null ? '添加二级领域' : '编辑二级领域')}
        open={domainModalVisible}
        onCancel={() => { setDomainModalVisible(false); domainForm.resetFields(); }}
        onOk={() => domainForm.submit()}
        destroyOnClose
      >
        <Form form={domainForm} layout="vertical" onFinish={(values: any) => {
          const updated = { ...selectedRecord };
          const payload = {
            domain_code: values.domain_code,
            domain_name: values.domain_name,
            priority: values.priority,
            knowledge_base_ids: values.knowledge_base_ids || [],
          };
          if (!updated.domains) updated.domains = [];
          if (editingDomainLevel === 'level1') {
            const list = [...(updated.domains || [])];
            if (editingDomainIndex === null) list.push({ ...payload, children: [] });
            else list[editingDomainIndex] = { ...(list[editingDomainIndex] || {}), ...payload, children: list[editingDomainIndex]?.children || [] };
            updated.domains = list;
          } else {
            if (editingDomainParentIndex === null) { message.error('未选择父级领域'); return; }
            const parent = { ...(updated.domains[editingDomainParentIndex] || { children: [] }) };
            const children = [...(parent.children || [])];
            if (editingDomainIndex === null) children.push(payload);
            else children[editingDomainIndex] = payload;
            parent.children = children;
            updated.domains[editingDomainParentIndex] = parent;
          }
          setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
          setSelectedRecord(updated);
          setDomainModalVisible(false);
          domainForm.resetFields();
          message.success('领域已保存');
        }}>
          <Form.Item label="领域代码" name="domain_code" rules={[{ required: true, message: '请输入领域代码' }]}>
            <Input placeholder="如：platform_intro" />
          </Form.Item>
          <Form.Item label="领域名称" name="domain_name" rules={[{ required: true, message: '请输入领域名称' }]}>
            <Input placeholder="如：平台介绍" />
          </Form.Item>
          <Form.Item label="优先级" name="priority" rules={[{ required: true, message: '请输入优先级' }]}>
            <InputNumber min={1} max={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="关联知识库ID（多选）" name="knowledge_base_ids">
            <Select mode="tags" placeholder="如：kb_platform_001, kb_activity_001" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 风格编辑弹窗 */}
      <Modal
        title={editingStyleIndex === null ? '添加风格' : '编辑风格'}
        open={styleModalVisible}
        onCancel={() => { setStyleModalVisible(false); styleForm.resetFields(); }}
        onOk={() => styleForm.submit()}
        destroyOnClose
      >
        <Form form={styleForm} layout="vertical" onFinish={(values: any) => {
          const updated = { ...selectedRecord };
          const payload = {
            style_code: values.style_code,
            style_name: values.style_name,
            weight: values.weight,
            script_ids: values.script_ids || [],
          };
          if (!updated.styles) updated.styles = { primary: [], secondary: [] };
          if (editingStyleType === 'primary') {
            const list = [...(updated.styles.primary || [])];
            if (editingStyleIndex === null) list.push(payload); else list[editingStyleIndex] = payload;
            updated.styles.primary = list;
          } else {
            const list = [...(updated.styles.secondary || [])];
            if (editingStyleIndex === null) list.push(payload); else list[editingStyleIndex] = payload;
            updated.styles.secondary = list;
          }
          setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
          setSelectedRecord(updated);
          setStyleModalVisible(false);
          styleForm.resetFields();
          message.success('风格已保存');
        }}>
          <Form.Item label="风格代码" name="style_code" rules={[{ required: true, message: '请输入风格代码' }]}>
            <Input placeholder="如：official" />
          </Form.Item>
          <Form.Item label="风格名称" name="style_name" rules={[{ required: true, message: '请输入风格名称' }]}>
            <Input placeholder="如：官方风格" />
          </Form.Item>
          <Form.Item label="权重%" name="weight" rules={[{ required: true, message: '请输入权重' }]}>
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="关联话术ID（多选）" name="script_ids">
            <Select mode="tags" placeholder="如：MSG041, MSG042" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* 调度配置模态框 */}
      <Modal
        title={selectedRecord ? `智能体详情 - ${selectedRecord.name}` : '智能体详情'}
        open={configModalVisible}
        onCancel={() => setConfigModalVisible(false)}
        width={1000}
        footer={[
          <Button key="cancel" onClick={() => setConfigModalVisible(false)}>
            关闭
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="domains_view">
          <Tabs.TabPane tab="基本信息" key="basic_info">
            {selectedRecord && (
              <div>
                <Descriptions column={2} bordered>
                  <Descriptions.Item label="智能体名称">{selectedRecord.name}</Descriptions.Item>
                  <Descriptions.Item label="智能体编码">{selectedRecord.code || selectedRecord.agentId}</Descriptions.Item>
                  <Descriptions.Item label="智能体类型">{selectedRecord.type}</Descriptions.Item>
                  <Descriptions.Item label="状态">
                    <Tag color={selectedRecord.status === 'online' ? 'green' : 'red'}>
                      {selectedRecord.status === 'online' ? '在线' : '离线'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="描述" span={2}>{selectedRecord.description}</Descriptions.Item>
                  <Descriptions.Item label="创建时间">{selectedRecord.createdAt}</Descriptions.Item>
                  <Descriptions.Item label="最后活动">{selectedRecord.lastActive || '-'}</Descriptions.Item>
                </Descriptions>
              </div>
            )}
          </Tabs.TabPane>
          <Tabs.TabPane tab="领域配置" key="domains_view">
            {selectedRecord ? (
              <div>
                <Space style={{ marginBottom: 12 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingDomainLevel('level1');
                    setEditingDomainIndex(null);
                    setEditingDomainParentIndex(null);
                    domainForm.resetFields();
                    domainForm.setFieldsValue({ priority: 10 });
                    setDomainModalVisible(true);
                  }}>添加一级领域</Button>
                </Space>
                <Divider />
                <h4>一级领域</h4>
                <List
                  size="small"
                  dataSource={selectedRecord?.domains || []}
                  renderItem={(d: any, idx: number) => (
                    <List.Item
                      actions={[
                        <Button type="link" icon={<PlusOutlined />} onClick={() => {
                          setEditingDomainLevel('level2');
                          setEditingDomainParentIndex(idx);
                          setEditingDomainIndex(null);
                          domainForm.resetFields();
                          domainForm.setFieldsValue({ priority: 8 });
                          setDomainModalVisible(true);
                        }}>添加二级领域</Button>,
                        <Button type="link" icon={<EditOutlined />} onClick={() => {
                          setEditingDomainLevel('level1');
                          setEditingDomainParentIndex(null);
                          setEditingDomainIndex(idx);
                          domainForm.setFieldsValue({ ...d });
                          setDomainModalVisible(true);
                        }}>编辑</Button>,
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => {
                          const updated = { ...selectedRecord };
                          updated.domains = (updated.domains || []).filter((_: any, i: number) => i !== idx);
                          setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
                          setSelectedRecord(updated);
                          message.success('已删除一级领域');
                        }}>删除</Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Tag color="red">{d.domain_name}</Tag>
                            <Tag>优先级: {d.priority}</Tag>
                          </Space>
                        }
                        description={`代码: ${d.domain_code}`}
                      />
                      <div>
                        {(d.knowledge_base_ids || []).map((kb: string, i: number) => (
                          <Tag key={i} color="blue">{kb}</Tag>
                        ))}
                      </div>
                    </List.Item>
                  )}
                />
                <Divider />
                <h4>二级领域</h4>
                <List
                  size="small"
                  dataSource={(selectedRecord?.domains || []).flatMap((p: any, pIdx: number) =>
                    (p.children || []).map((c: any, cIdx: number) => ({ c, cIdx, pIdx, parent: p }))
                  )}
                  renderItem={(row: any) => (
                    <List.Item
                      actions={[
                        <Button type="link" icon={<EditOutlined />} onClick={() => {
                          setEditingDomainLevel('level2');
                          setEditingDomainParentIndex(row.pIdx);
                          setEditingDomainIndex(row.cIdx);
                          domainForm.setFieldsValue({ ...row.c });
                          setDomainModalVisible(true);
                        }}>编辑</Button>,
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => {
                          const updated = { ...selectedRecord };
                          const parent = updated.domains[row.pIdx];
                          parent.children = (parent.children || []).filter((_: any, i: number) => i !== row.cIdx);
                          setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
                          setSelectedRecord(updated);
                          message.success('已删除二级领域');
                        }}>删除</Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Tag color="blue">{row.c.domain_name}</Tag>
                            <Tag>优先级: {row.c.priority}</Tag>
                          </Space>
                        }
                        description={`父级: ${row.parent.domain_name} / 代码: ${row.c.domain_code}`}
                      />
                      <div>
                        {(row.c.knowledge_base_ids || []).map((kb: string, i: number) => (
                          <Tag key={i}>{kb}</Tag>
                        ))}
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            ) : null}
          </Tabs.TabPane>
          <Tabs.TabPane tab="风格配置" key="styles_view">
            {selectedRecord ? (
              <div>
                <Space style={{ marginBottom: 12 }}>
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                    setEditingStyleType('primary');
                    setEditingStyleIndex(null);
                    styleForm.resetFields();
                    styleForm.setFieldsValue({ weight: 50 });
                    setStyleModalVisible(true);
                  }}>添加主要风格</Button>
                  <Button icon={<PlusOutlined />} onClick={() => {
                    setEditingStyleType('secondary');
                    setEditingStyleIndex(null);
                    styleForm.resetFields();
                    styleForm.setFieldsValue({ weight: 10 });
                    setStyleModalVisible(true);
                  }}>添加副风格</Button>
                </Space>
                <Divider />
                <h4>主要风格</h4>
                <List
                  size="small"
                  dataSource={selectedRecord?.styles?.primary || []}
                  renderItem={(s: any, idx: number) => (
                    <List.Item
                      actions={[
                        <Button type="link" icon={<EditOutlined />} onClick={() => {
                          setEditingStyleType('primary');
                          setEditingStyleIndex(idx);
                          styleForm.setFieldsValue({ ...s });
                          setStyleModalVisible(true);
                        }}>编辑</Button>,
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => {
                          const updated = { ...selectedRecord };
                          updated.styles.primary = (updated.styles.primary || []).filter((_: any, i: number) => i !== idx);
                          setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
                          setSelectedRecord(updated);
                          message.success('已删除风格');
                        }}>删除</Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Tag color="red">{s.style_name}</Tag>
                            <Tag color="gold">权重: {s.weight}%</Tag>
                          </Space>
                        }
                        description={`代码: ${s.style_code}`}
                      />
                      <div>
                        {(s.script_ids || []).map((sid: string, i: number) => (
                          <Tag key={i}>{sid}</Tag>
                        ))}
                      </div>
                    </List.Item>
                  )}
                />
                <Divider />
                <h4>副风格</h4>
                <List
                  size="small"
                  dataSource={selectedRecord?.styles?.secondary || []}
                  renderItem={(s: any, idx: number) => (
                    <List.Item
                      actions={[
                        <Button type="link" icon={<EditOutlined />} onClick={() => {
                          setEditingStyleType('secondary');
                          setEditingStyleIndex(idx);
                          styleForm.setFieldsValue({ ...s });
                          setStyleModalVisible(true);
                        }}>编辑</Button>,
                        <Button type="link" danger icon={<DeleteOutlined />} onClick={() => {
                          const updated = { ...selectedRecord };
                          updated.styles.secondary = (updated.styles.secondary || []).filter((_: any, i: number) => i !== idx);
                          setAgents(prev => prev.map(a => a.id === updated.id ? updated : a));
                          setSelectedRecord(updated);
                          message.success('已删除风格');
                        }}>删除</Button>
                      ]}
                    >
                      <List.Item.Meta
                        title={
                          <Space>
                            <Tag color="blue">{s.style_name}</Tag>
                            <Tag color="gold">权重: {s.weight}%</Tag>
                          </Space>
                        }
                        description={`代码: ${s.style_code}`}
                      />
                      <div>
                        {(s.script_ids || []).map((sid: string, i: number) => (
                          <Tag key={i}>{sid}</Tag>
                        ))}
                      </div>
                    </List.Item>
                  )}
                />
              </div>
            ) : null}
          </Tabs.TabPane>
          <Tabs.TabPane tab="性能监控" key="2">
            <div>
              <h4>性能指标</h4>
              <List
                size="small"
                dataSource={[
                  { agent: '气氛活跃Bot', responseTime: 1.2, successRate: 94.5, concurrent: 3 },
                  { agent: '产品介绍Bot', responseTime: 1.5, successRate: 92.3, concurrent: 2 },
                  { agent: '客服Bot', responseTime: 0.8, successRate: 98.7, concurrent: 8 },
                  { agent: '情感交流Bot', responseTime: 2.1, successRate: 89.2, concurrent: 1 },
                ]}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.agent}
                      description={
                        <Space>
                          <span>响应时间: {item.responseTime}s</span>
                          <span>成功率: {item.successRate}%</span>
                          <span>并发数: {item.concurrent}</span>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab="协作配置" key="3">
            <div>
              <h4>多智能体协作</h4>
              <Row gutter={16}>
                <Col span={12}>
                  <label>协作模式：</label>
                  <Select defaultValue="sequential" style={{ width: 150, marginLeft: 8 }}>
                    <Option value="sequential">顺序执行</Option>
                    <Option value="parallel">并行执行</Option>
                    <Option value="conditional">条件执行</Option>
                  </Select>
                </Col>
                <Col span={12}>
                  <label>上下文传递：</label>
                  <Switch defaultChecked />
                </Col>
              </Row>
              <Divider />
              <div>
                <h4>协作规则</h4>
                <List
                  size="small"
                  dataSource={[
                    { rule: '客服Bot优先处理问题', condition: '用户问题类型=客服' },
                    { rule: '气氛活跃Bot处理冷场', condition: '对话间隔>5分钟' },
                    { rule: '情感交流Bot处理情绪', condition: '用户情绪=负面' },
                  ]}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        title={item.rule}
                        description={item.condition}
                      />
                    </List.Item>
                  )}
                />
              </div>
            </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>

      {/* 智能体测试模态框 */}
      <Modal
        title="智能体测试"
        open={testModalVisible}
        onCancel={() => setTestModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setTestModalVisible(false)}>
            关闭
          </Button>,
          <Button 
            key="test" 
            type="primary" 
            icon={<ExperimentOutlined />}
            onClick={handleRunTest}
            loading={loading}
          >
            运行测试
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Alert
              message="智能体测试"
              description={`正在测试智能体：${selectedRecord.name} (${selectedRecord.type})`}
              type="info"
              style={{ marginBottom: 16 }}
            />
            
            <Steps
              current={currentStep}
              items={[
                { title: '初始化智能体' },
                { title: '加载知识库' },
                { title: '执行对话测试' },
                { title: '验证响应质量' },
                { title: '生成测试报告' },
              ]}
              style={{ marginBottom: 24 }}
            />
            
            {testResults.length > 0 && (
              <div>
                <h4>测试结果：</h4>
                <List
                  dataSource={testResults}
                  renderItem={(item) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                        title={item.title}
                        description={`步骤 ${item.step + 1} - 执行成功`}
                      />
                      <Tag color={item.status === 'finish' ? 'green' : 'blue'}>
                        {item.status === 'finish' ? '完成' : '进行中'}
                      </Tag>
                    </List.Item>
                  )}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 知识库管理模态框 */}
      <Modal
        title="知识库管理"
        open={knowledgeModalVisible}
        onCancel={() => setKnowledgeModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setKnowledgeModalVisible(false)}>
            关闭
          </Button>,
          <Button key="save" type="primary">
            保存更改
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="知识文档" key="1">
                <div>
                  <Space style={{ marginBottom: 16 }}>
                    <Button icon={<PlusOutlined />} type="primary">
                      添加文档
                    </Button>
                    <Upload>
                      <Button icon={<UploadOutlined />}>上传文档</Button>
                    </Upload>
                  </Space>
                  <List
                    dataSource={[
                      { id: 1, title: '产品手册.pdf', size: '2.5MB', status: '已索引' },
                      { id: 2, title: '常见问题.docx', size: '1.2MB', status: '已索引' },
                      { id: 3, title: '用户指南.md', size: '800KB', status: '索引中' },
                    ]}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Button type="link">查看</Button>,
                          <Button type="link">编辑</Button>,
                          <Button type="link" danger>删除</Button>,
                        ]}
                      >
                        <List.Item.Meta
                          avatar={<FileTextOutlined style={{ fontSize: 24 }} />}
                          title={item.title}
                          description={`大小: ${item.size} | 状态: ${item.status}`}
                        />
                      </List.Item>
                    )}
                  />
                </div>
              </Tabs.TabPane>
              
              <Tabs.TabPane tab="知识库配置" key="2">
                <Form layout="vertical">
                  <Form.Item label="向量化模型">
                    <Select defaultValue="text-embedding-ada-002">
                      <Option value="text-embedding-ada-002">OpenAI Ada-002</Option>
                      <Option value="text-embedding-3-small">OpenAI Embedding-3-Small</Option>
                      <Option value="bge-large">BGE-Large</Option>
                    </Select>
                  </Form.Item>
                  
                  <Form.Item label="相似度阈值">
                    <Slider 
                      defaultValue={0.7} 
                      min={0} 
                      max={1} 
                      step={0.05}
                      marks={{
                        0: '0',
                        0.5: '0.5',
                        1: '1.0'
                      }}
                    />
                  </Form.Item>
                  
                  <Form.Item label="最大检索数量">
                    <InputNumber defaultValue={5} min={1} max={20} />
                  </Form.Item>
                </Form>
              </Tabs.TabPane>
            </Tabs>
          </div>
        )}
      </Modal>

      {/* 部署智能体模态框 */}
      <Modal
        title="部署智能体"
        open={deployModalVisible}
        onCancel={() => setDeployModalVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setDeployModalVisible(false)}>
            取消
          </Button>,
          <Button key="deploy" type="primary" icon={<PlayCircleOutlined />}>
            开始部署
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Alert
              message="部署确认"
              description={`即将部署智能体：${selectedRecord.name}`}
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Form layout="vertical">
              <Form.Item label="部署环境">
                <Radio.Group defaultValue="production">
                  <Radio value="development">开发环境</Radio>
                  <Radio value="staging">测试环境</Radio>
                  <Radio value="production">生产环境</Radio>
                </Radio.Group>
              </Form.Item>
              
              <Form.Item label="最大并发数">
                <InputNumber defaultValue={selectedRecord.maxConcurrent} min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
              
              <Form.Item label="超时时间(秒)">
                <InputNumber defaultValue={30} min={5} max={300} style={{ width: '100%' }} />
              </Form.Item>
              
              <Form.Item label="自动重试">
                <Switch defaultChecked />
              </Form.Item>
              
              <Form.Item label="部署说明">
                <TextArea rows={3} placeholder="输入本次部署的说明..." />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentManagement;
